import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { API_BASE } from '../config/api';

const API = API_BASE;
const SiteContentContext = createContext({});

export const useSiteContent = () => useContext(SiteContentContext);

// Defaults mirror the seeded data so the page never looks blank during load
const DEFAULTS = {
  homepage_hero: {
    badge_text: 'For Beauty Professionals',
    heading_line1: 'Build Your',
    heading_line2: 'Beauty Empire',
    description: 'Coaching and systems designed for salon owners and beauty professionals ready to scale.',
  },
  about_natasha: {
    overline: 'About Natasha',
    heading: 'Coach. Speaker. Author.',
    description: 'With deep roots in the beauty industry, Natasha Simone has dedicated her career to empowering salon owners, beauty professionals, and individuals to unlock their full potential. Through strategic coaching, hands-on workshops, and her bestselling book, she brings clarity, confidence, and proven systems to everyone she works with.',
  },
  business_hero: {
    badge_text: 'For Salon Owners & Beauty Professionals',
    heading_line1: 'Scale Your Salon with',
    heading_line2: 'Proven Systems',
    description: 'Stop working in your business and start working on it. Coaching and systems designed specifically for beauty professionals ready to grow.',
  },
  business_solution: {
    overline: 'The Solution',
    heading: 'Diverse Intelligence Systems',
    description1: 'A structured system built specifically for beauty professionals to scale their salon, build stronger teams, and grow revenue predictably.',
    description2: 'Move from overwhelmed to organized with frameworks designed for the beauty industry by someone who understands it.',
  },
  personal_hero: {
    badge_text: 'Personal Development',
    heading_line1: 'Become the Most',
    heading_line2: 'Confident You',
    description: 'Growth starts from within. Build confidence, clarity, and purpose through mindset coaching and personal development.',
  },
  book_section: {
    heading: 'The Beauty of You',
    description1: 'A personal development book designed to help you discover your confidence, build self-awareness, and step into your full potential.',
    description2: 'Through practical exercises, reflections, and personal insights, this book guides you on a transformative journey of self-discovery.',
    price: '19.99',
    stripe_link: '',
  },
  testimonials: [
    { name: 'Sarah Mitchell', role: 'Salon Owner, Florida', quote: 'Natasha transformed how I run my salon. Her systems helped me increase revenue by 40% while working fewer hours.', rating: 5, page: 'home' },
    { name: 'Jessica Thompson', role: 'Beauty Studio Director', quote: 'The coaching gave me the confidence to lead my team effectively. Best investment I\'ve made in my business.', rating: 5, page: 'home' },
    { name: 'Amanda Richards', role: 'Stylist & Entrepreneur', quote: 'Her book changed my mindset completely. I finally feel like I\'m building something meaningful.', rating: 5, page: 'home' },
    { name: 'Rebecca Chen', role: 'Owner, Luxe Hair Studio', quote: 'Within 6 months of working with Natasha, my salon revenue increased by 35%. Her systems approach completely changed how I manage my team and bookings.', rating: 5, page: 'business' },
    { name: 'Michelle Okonkwo', role: 'Founder, Glow Beauty Collective', quote: 'I finally have clarity on how to scale without burning out. The DIS program gave me the framework I needed to step back from the chair and lead my business.', rating: 5, page: 'business' },
    { name: 'Lauren Davis', role: 'Workshop Participant', quote: 'Reading \'The Beauty of You\' helped me finally see my worth. I stopped doubting myself and started showing up with confidence\u2014in my career and in my life.', rating: 5, page: 'personal' },
  ],
  contact_details: { email: 'hello@iamnatashasimone.com', phone: '', calendly_url: '' },
  social_links: { instagram: 'https://www.instagram.com/iamnatashasimone/', facebook: 'https://www.facebook.com/iamnatashasimone/', tiktok: '', linkedin: '' },
  programs: [
    { title: 'Business Assessment', description: 'Comprehensive audit of your salon bookings, pricing, operations, and growth opportunities.', icon: 'BarChart3' },
    { title: 'DIS Program', description: 'Complete system to scale your salon operations, team, and revenue predictably.', icon: 'Briefcase' },
    { title: 'Consulting & Coaching', description: 'Personalized 1:1 support for salon strategy, planning, and execution.', icon: 'Users' },
    { title: 'Team Workshops', description: 'On-site training for salon teams focused on performance and growth.', icon: 'Calendar' },
  ],
};

export const SiteContentProvider = ({ children }) => {
  const [content, setContent] = useState(DEFAULTS);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    axios.get(`${API}/api/content`)
      .then(({ data }) => {
        const merged = { ...DEFAULTS };
        for (const key of Object.keys(data)) {
          merged[key] = data[key];
        }
        setContent(merged);
      })
      .catch((err) => {
        console.error('Failed to load site content:', err.message);
      })
      .finally(() => setLoaded(true));
  }, []); // API and DEFAULTS are module-level constants — stable across renders

  const get = useCallback((key) => content[key] || DEFAULTS[key] || {}, [content]);

  const testimonials = useCallback((page) => {
    const all = content.testimonials || DEFAULTS.testimonials;
    if (!page) return all;
    return all.filter((t) => t.page === page);
  }, [content]);

  const value = useMemo(
    () => ({ content, get, testimonials, loaded }),
    [content, get, testimonials, loaded]
  );

  return (
    <SiteContentContext.Provider value={value}>
      {children}
    </SiteContentContext.Provider>
  );
};
